// row-context-menu-close.ts — PSTUDIO-1433 (crash fix)

/**
 * The two menus spell their open state differently — the row menu uses `open`
 * (`draggable-row.tsx`), the cell menu `opened` (`cell.tsx`) — so the resolver
 * accepts both rather than forcing one of them to rename its state.
 */
export type RowContextMenuState = 'closed' | 'closing' | 'open' | 'opened';

/**
 * Resolve the DELAYED close of a row context menu.
 *
 * The menu is unmounted when its state reaches `closed`, and its `ps-dropdown`
 * host physically MOVES ITSELF into the popover portal while open (see
 * `popover-base.wc.ts` — `projectedContent = this`). Unmounting a menu that is
 * still open therefore makes React call `removeChild` on a node whose real
 * parent is the portal slot, not the `<td>` React expects:
 * `NotFoundError: The node to be removed is not a child of this node`, which
 * takes the entire page down to the error boundary.
 *
 * Since PSTUDIO-4229 the popover DOES return that node on every close, not only
 * while it is top of `popoverItemsSignal` — but the window is still real, and
 * the delay below is still needed, for a different reason: the return runs in
 * Lit's `updated()`, one asynchronous update cycle AFTER `active` is set to
 * false. React can unmount the row inside that cycle, i.e. while the host is
 * still parked in the portal. So "the popover always un-projects" is a promise
 * about WHETHER, never about WHEN.
 *
 * The close is delayed so the popover's own close animation can finish, and the
 * delay is what opens the window: right-click (open) → click a menu item
 * (closing, schedules the close) → right-click again (open) → the stale timer
 * fires and forces `closed` on a menu that is live again. Measured in the
 * browser on 29 Aug 2026 in exactly that order, immediately before the crash.
 *
 * So the delayed close only completes a close that is STILL pending; any other
 * state means the menu moved on and must be left alone. The caller also cancels
 * the timer on cleanup — this guard is the second line of defence, for a timer
 * that already fired before the cleanup ran.
 */
export function resolveDelayedContextMenuClose<T extends RowContextMenuState>(
  current: T
): T | 'closed' {
  return current === 'closing' ? 'closed' : current;
}
